/**
 * Build O(1) lookup maps for Domain Mapping / KPI Registry joins.
 * IDs are stringified so numeric API ids and string local ids join reliably.
 */
export function buildConfigureIndexes({
    businesses = [],
    functions = [],
    kpis = [],
    models = [],
    assets = [],
    mappings = []
} = {}) {
    const toKey = (id) => (id === null || id === undefined ? id : String(id));

    const businessById = new Map(businesses.map(b => [toKey(b.id), b]));
    const functionById = new Map(functions.map(f => [toKey(f.id), f]));
    const kpiById = new Map(kpis.map(k => [toKey(k.id), k]));
    const modelById = new Map(models.map(m => [toKey(m.id), m]));
    const assetById = new Map(assets.map(a => [toKey(a.id), a]));

    const functionsByBusinessId = new Map();
    functions.forEach(f => {
        const key = toKey(f.businessId);
        if (!functionsByBusinessId.has(key)) functionsByBusinessId.set(key, []);
        functionsByBusinessId.get(key).push(f);
    });

    const kpisByFunctionId = new Map();
    kpis.forEach(k => {
        const key = toKey(k.functionId);
        if (!kpisByFunctionId.has(key)) kpisByFunctionId.set(key, []);
        kpisByFunctionId.get(key).push(k);
    });

    const modelsByFunctionId = new Map();
    models.forEach(m => {
        const key = toKey(m.functionId);
        if (!modelsByFunctionId.has(key)) modelsByFunctionId.set(key, []);
        modelsByFunctionId.get(key).push(m);
    });

    const mappingCountByKpiId = new Map();
    const mappingCountByModelId = new Map();
    const mappingByKpiAsset = new Map();

    mappings.forEach(m => {
        if (m.kpiId) {
            const kpiKey = toKey(m.kpiId);
            mappingCountByKpiId.set(kpiKey, (mappingCountByKpiId.get(kpiKey) || 0) + 1);
            if (m.assetId) {
                mappingByKpiAsset.set(`${toKey(m.kpiId)}::${toKey(m.assetId)}`, m);
            }
        }
        if (m.modelId) {
            const modelKey = toKey(m.modelId);
            mappingCountByModelId.set(modelKey, (mappingCountByModelId.get(modelKey) || 0) + 1);
        }
    });

    const getBiz = id => businessById.get(toKey(id));
    const getFn = id => functionById.get(toKey(id));
    const getKpi = id => kpiById.get(toKey(id));
    const getModel = id => modelById.get(toKey(id));
    const getAsset = id => assetById.get(toKey(id));

    const isBizEff = b => !!(b && b.active);
    const isFnEff = f => !!(f && f.active && isBizEff(getBiz(f.businessId)));
    const isKpiEff = k => !!(k && k.active && isFnEff(getFn(k.functionId)));
    const isModelEff = m => !!(m && m.active && isFnEff(getFn(m.functionId)));

    const resolveMappingContext = (mapping) => {
        const kpi = mapping.kpiId ? getKpi(mapping.kpiId) : null;
        const model = !kpi && mapping.modelId ? getModel(mapping.modelId) : null;
        const item = kpi || model;
        if (!item) return null;
        const fn = getFn(item.functionId);
        const biz = fn ? getBiz(fn.businessId) : null;
        const asset = mapping.assetId ? getAsset(mapping.assetId) : null;
        const effectivelyActive = kpi ? isKpiEff(kpi) : isModelEff(model);
        return { kpi, model, item, fn, biz, asset, effectivelyActive, isModel: !!model };
    };

    return {
        businessById,
        functionById,
        kpiById,
        modelById,
        assetById,
        functionsByBusinessId,
        kpisByFunctionId,
        modelsByFunctionId,
        mappingCountByKpiId,
        mappingCountByModelId,
        mappingByKpiAsset,
        getBiz,
        getFn,
        getKpi,
        getModel,
        getAsset,
        isBizEff,
        isFnEff,
        isKpiEff,
        isModelEff,
        resolveMappingContext,
        getFnsOf: bid => functionsByBusinessId.get(toKey(bid)) || [],
        getKpisOf: fid => kpisByFunctionId.get(toKey(fid)) || [],
        getModelsOf: fid => modelsByFunctionId.get(toKey(fid)) || [],
        getMappingCount: (itemId, isModel) =>
            isModel
                ? (mappingCountByModelId.get(toKey(itemId)) || 0)
                : (mappingCountByKpiId.get(toKey(itemId)) || 0)
    };
}

export function mappingPairKey(kpiId, assetId) {
    return `${kpiId}::${assetId}`;
}

/**
 * Upsert mappings by unique (kpiId, assetId).
 * Optionally remove one mapping (replaceMappingId) or all mappings for a KPI (replaceKpiId).
 */
export function upsertMappings(existingMappings, incomingMappings, { replaceMappingId = null, replaceKpiId = null } = {}) {
    const next = existingMappings.filter(m => {
        if (replaceKpiId && m.kpiId === replaceKpiId) return false;
        if (replaceMappingId && m.id === replaceMappingId) return false;
        return true;
    });
    const byPair = new Map();
    next.forEach(m => {
        if (m.kpiId && m.assetId) {
            byPair.set(mappingPairKey(m.kpiId, m.assetId), m);
        }
    });

    incomingMappings.forEach(incoming => {
        const key = mappingPairKey(incoming.kpiId, incoming.assetId);
        const existing = byPair.get(key);
        if (existing) {
            const idx = next.findIndex(m => m.id === existing.id);
            if (idx > -1) {
                next[idx] = { ...existing, ...incoming, id: existing.id };
                byPair.set(key, next[idx]);
            }
        } else {
            next.push(incoming);
            byPair.set(key, incoming);
        }
    });

    return next;
}
